import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { z } from "zod";

import { useFeatureIsOn } from "@/app/featureFlags";
import { BannersPlacement } from "@/components/BannersPlacement/BannersPlacement";
import { PromoCodeForm } from "@/components/PromoCodeForm/PromoCodeForm";
import { usePaymentContext } from "@/contexts/PaymentContext";
import BottomDrawer from "@/features/flights/components/flight-purchase/BottomDrawer";
import InstallmentDetails from "@/features/flights/components/flight-purchase/InstallmentDetails";
import { HotelAndRoomCard } from "@/features/hotels/components/hotel/HotelAndRoomCard";
import { MainTravellerRadioButtons } from "@/features/hotels/components/hotel/MainTravellerRadioButtons";
import { TermsAndConditions } from "@/features/hotels/components/hotel/TermsAndConditions";
import type { HotelBooking, HotelData } from "@/features/hotels/types/types";
import { calculateBookingPrice } from "@/features/hotels/utils/calculateBookingPrice";
import { getTravellerTypeAtTravelDate } from "@/features/travellers/utils/ageValidation";
import type { ValidationStatus } from "@/hooks/usePromoCodeValidation";
import { PromoCodeTrackingEvents, rudderAnalytics } from "@/lib/rudderstack";
import type {
  FindUserTravelersTravelers,
  ValidateHotelPromoCodeOutputData,
} from "@/types/codegen";
import { BannersPlacementSlug } from "@/types/enums";
import { includeBnplFee, calculateMonthlyInstallment } from "@/utils/bnpl-fee";
import { formatCurrency } from "@/utils/currency";

export interface SummaryFormValues {
  leadTraveler: string;
  termsAndConditions: boolean;
}

export interface HotelBookingSummaryFormProps {
  booking: HotelBooking;
  travelDate: Date;
  promoCode: string | null;
  promoCodeData: ValidateHotelPromoCodeOutputData | undefined;
  promoCodeValidationStatus: ValidationStatus | undefined;
  setPromoCode: (code: string | null) => void;
  hotel: HotelData;
  loading: boolean;
  disabledSubmit?: boolean;
  rooms?: { adults: number; children: number }[];
  selectedTravellers: FindUserTravelersTravelers[];
  handleSubmit: (values: SummaryFormValues) => void;
}

export const HotelBookingSummaryForm = ({
  booking,
  travelDate,
  promoCode,
  promoCodeData,
  promoCodeValidationStatus,
  setPromoCode,
  hotel,
  loading,
  rooms = [],
  disabledSubmit,
  selectedTravellers,
  handleSubmit: onSubmit,
}: HotelBookingSummaryFormProps) => {
  const intl = useIntl();
  const { paymentType, selectedInstallments } = usePaymentContext();

  const hotelTotalPrice = calculateBookingPrice(booking);

  const totalPrice =
    paymentType === "bnpl"
      ? includeBnplFee(hotelTotalPrice.value)
      : hotelTotalPrice.value;
  const totalPriceWithPromoCode = promoCodeData?.paymentAmount || totalPrice;

  const validationSchema = z.object({
    termsAndConditions: z.boolean().refine((v) => v === true),
    leadTraveler: z.string().min(1),
  });

  const leadTraveler = selectedTravellers.find(
    (traveller) =>
      getTravellerTypeAtTravelDate(traveller.birthDate, travelDate) === "adult",
  );

  const methods = useForm<
    z.input<typeof validationSchema>,
    undefined,
    z.infer<typeof validationSchema>
  >({
    defaultValues: {
      leadTraveler: leadTraveler?.id || "",
      termsAndConditions: false,
    },
    resolver: zodResolver(validationSchema),
  });

  const {
    register,
    control,
    handleSubmit,
    formState: { isValid, isDirty },
  } = methods;

  return (
    <FormProvider {...methods}>
      <div className="flex w-full flex-col">
        <HotelAndRoomCard
          hotel={hotel}
          hotelBookingData={booking}
          rooms={rooms}
        />

        <div className="section-title mt-4">
          {intl.formatMessage({
            defaultMessage: "Main traveller",
            description:
              "Badge or label identifying the primary / main traveller in a booking",
          })}
        </div>
        <div className="section-card p-0">
          <div className="p-4 pb-2 text-sm text-text-tertiary">
            {intl.formatMessage({
              defaultMessage:
                "Please select the traveller which will be marked as the main",
              description:
                "Option or button to designate a traveller as the main / primary traveller",
            })}
          </div>

          <Controller
            control={control}
            name="leadTraveler"
            render={({ field }) => (
              <MainTravellerRadioButtons
                {...field}
                travelDate={travelDate}
                selectedTravellers={selectedTravellers}
              />
            )}
          />
        </div>

        <div className="section-card mt-2">
          <div className="text-sm text-text-title">
            {intl.formatMessage({
              defaultMessage:
                "The reservation policy is subject to the rules listed below, and these rules may differ from the policies listed by the hotel.",
              description:
                "Disclaimer that the cancellation / refund policy shown may differ from the hotel's own policy",
            })}
          </div>
        </div>

        <BannersPlacement
          slug={BannersPlacementSlug.CHECKOUT}
          classNames={{
            wrapper: "-mx-4 mt-4",
          }}
        />

        {useFeatureIsOn("promo-codes") ? (
          <div className="mt-4">
            <PromoCodeForm
              appliedCode={promoCode || undefined}
              validationStatus={promoCodeValidationStatus}
              onCodeApply={({ code }) => {
                rudderAnalytics.track(
                  PromoCodeTrackingEvents.USER_SUBMIT_PROMO_CODE,
                  { promo_code: code, flow_type: "hotel" },
                );

                setPromoCode(code);
              }}
              onCodeRemove={({ code, isApplied }) => {
                if (isApplied) {
                  rudderAnalytics.track(
                    PromoCodeTrackingEvents.USER_REMOVE_PROMO_CODE,
                    {
                      promo_code: code,
                      discount_amount_removed: promoCodeData?.result
                        ? (promoCodeData.discountAmount ?? null)
                        : null,
                      flow_type: "hotel",
                    },
                  );
                }

                setPromoCode(null);
              }}
              onFocus={(hasAppliedPromoCode) => {
                if (!hasAppliedPromoCode) {
                  rudderAnalytics.track(
                    PromoCodeTrackingEvents.USER_SELECTED_PROMO_CODE,
                    { flow_type: "hotel" },
                  );
                }
              }}
            />
          </div>
        ) : null}

        <div className="section-title mt-4">
          {intl.formatMessage({
            defaultMessage: "Summary",
            description: "Section heading label for a booking or order summary",
          })}
        </div>
        <div className="section-card">
          <TermsAndConditions {...register("termsAndConditions")} />
        </div>

        {paymentType === "bnpl" ? (
          <InstallmentDetails
            promoCodeDiscount={promoCodeData?.discountAmount}
            totalPrice={totalPriceWithPromoCode}
          />
        ) : (
          <div className="section-card mt-2 flex flex-col gap-2 text-sm">
            {promoCodeData?.discountAmount ? (
              <div className="flex justify-between">
                <div className="text-text-tertiary">
                  {intl.formatMessage({
                    defaultMessage: "Promo code discount",
                    description:
                      "Line item label for the promo code discount in the price summary",
                  })}
                </div>
                <div className="font-bold text-core-accent-500">
                  {formatCurrency(promoCodeData?.discountAmount)}
                </div>
              </div>
            ) : null}

            <div className="flex justify-between">
              <div className="text-text-tertiary">
                {intl.formatMessage({
                  defaultMessage: "Total",
                  description: "Generic 'Total' label in price summary tables",
                })}
              </div>
              <div className="font-bold text-text-primary">
                {formatCurrency(totalPriceWithPromoCode)}
              </div>
            </div>

            <div className="flex justify-between">
              <div className="text-text-tertiary">
                {intl.formatMessage({
                  defaultMessage: "Payment method",
                  description:
                    "Label for the selected payment method shown in the booking summary",
                })}
              </div>
              <div className="font-bold text-text-primary">
                {intl.formatMessage({
                  defaultMessage: "Qi",
                  description: "Payment method label for the QI card",
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      <BottomDrawer
        loading={loading}
        totalPrice={
          paymentType === "bnpl"
            ? undefined
            : formatCurrency(totalPriceWithPromoCode)
        }
        payLaterPrice={
          paymentType === "bnpl"
            ? formatCurrency(
                calculateMonthlyInstallment(
                  totalPriceWithPromoCode,
                  selectedInstallments,
                ),
              )
            : undefined
        }
        buttonLabel={intl.formatMessage({
          defaultMessage: "Book a room",
          description:
            "CTA button to start the room booking process on the hotel details page",
        })}
        onButtonClick={handleSubmit((data) => onSubmit(data))}
        disabled={
          disabledSubmit ||
          !isValid ||
          !isDirty ||
          promoCodeValidationStatus?.type === "loading"
        }
      />
    </FormProvider>
  );
};
